/**
 * @file useSwitchboardManagement.js
 * @description Switchboard hierarchy and allocation management
 *
 * Handles:
 * - Switchboard creation and addition
 * - Switchboard allocation to hierarchy
 * - Unallocated switchboard tracking
 * - Valid parent-child relationships
 * - Location picker for switchboards
 */

import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';

export function useSwitchboardManagement() {
  // ============================================================================
  // STATE
  // ============================================================================

  const $q = useQuasar();

  /**
   * Add switchboard dialog visibility
   * @type {Ref<boolean>}
   */
  const addSwitchboardDialogVisible = ref(false);

  /**
   * Add existing switchboard dialog visibility
   * @type {Ref<boolean>}
   */
  const addExistingSwitchboardDialogVisible = ref(false);

  /**
   * Location picker dialog visibility (for switchboards)
   * @type {Ref<boolean>}
   */
  const locationPickerDialogVisible = ref(false);

  /**
   * Parent node for new switchboard
   * @type {Ref<Object|null>}
   */
  const parentSwitchboardNode = ref(null);

  /**
   * New switchboard form data
   * @type {Ref<Object>}
   */
  const newSwitchboardData = ref({
    name: '',
    brick_class: 'brick:Electrical_Switchboard',
    classification: null,
    parent_switchboard_id: null,
    building_id: null,
    level_id: null,
    space_id: null,
  });

  /**
   * Selected existing switchboard for allocation
   * @type {Ref<number|null>}
   */
  const selectedExistingSwitchboard = ref(null);

  /**
   * Unallocated switchboards (not in hierarchy)
   * @type {Ref<Array>}
   */
  const unallocatedSwitchboards = ref([]);

  /**
   * Loading state for operations
   * @type {Ref<boolean>}
   */
  const savingSwitchboard = ref(false);

  /**
   * Current switchboard for location selection
   * @type {Ref<Object|null>}
   */
  const switchboardForLocation = ref(null);

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Check if can save new switchboard
   */
  const canSaveNewSwitchboard = computed(() => {
    return (
      newSwitchboardData.value.name &&
      newSwitchboardData.value.classification &&
      newSwitchboardData.value.brick_class
    );
  });

  /**
   * Count of unallocated switchboards
   */
  const unallocatedCount = computed(() => {
    return unallocatedSwitchboards.value.length;
  });

  /**
   * Available new switchboard types based on parent
   */
  const availableNewSwitchboardTypes = computed(() => {
    if (!parentSwitchboardNode.value) {return [];}

    const parentType = parentSwitchboardNode.value.type;

    const allTypes = [
      {
        value: 'main_switchboard',
        label: 'Main Switchboard (MSB)',
        icon: 'electrical_services',
        color: 'red',
      },
      {
        value: 'mechanical_switchboard',
        label: 'Mechanical Services Switchboard (MSSB)',
        icon: 'settings',
        color: 'orange',
      },
      {
        value: 'distribution_board',
        label: 'Distribution Board (DB)',
        icon: 'dashboard',
        color: 'blue',
      },
      {
        value: 'ddc_panel',
        label: 'DDC Panel',
        icon: 'developer_board',
        color: 'purple',
      },
    ];

    // Filter based on parent type
    switch (parentType) {
      case 'main_switchboard':
        return allTypes.filter(
          (t) => t.value === 'distribution_board' || t.value === 'ddc_panel',
        );

      case 'mechanical_switchboard':
        return allTypes.filter(
          (t) => t.value === 'distribution_board' || t.value === 'ddc_panel',
        );

      case 'distribution_board':
        return allTypes.filter((t) => t.value === 'ddc_panel');

      default:
        return [];
    }
  });

  // ============================================================================
  // ADD NEW SWITCHBOARD
  // ============================================================================

  /**
   * Show add switchboard dialog
   * @param {Object} parentNode - Parent tree node
   */
  function showAddSwitchboardDialog(parentNode) {

    parentSwitchboardNode.value = parentNode;

    // Reset form
    newSwitchboardData.value = {
      name: '',
      brick_class: 'brick:Electrical_Switchboard',
      classification: null,
      parent_switchboard_id: parentNode?.data?.id || null,
      building_id: parentNode?.data?.building_id || null,
      level_id: null,
      space_id: null,
    };

    addSwitchboardDialogVisible.value = true;
  }

  /**
   * Close add switchboard dialog
   */
  function closeAddSwitchboardDialog() {
    addSwitchboardDialogVisible.value = false;
    parentSwitchboardNode.value = null;
  }

  /**
   * Open add switchboard dialog with parent context
   * @param {Object} parentNode - Parent tree node
   */
  function openAddSwitchboardDialog(parentNode) {
    showAddSwitchboardDialog(parentNode);
  }

  /**
   * Save new switchboard
   * @param {Function} apiCallback - Async function to create via API
   * @returns {Promise<Object>} Created switchboard
   */
  async function saveNewSwitchboard(apiCallback) {
    if (!canSaveNewSwitchboard.value) {
      $q.notify({
        type: 'warning',
        message: 'Please fill in all required fields',
      });
      return null;
    }

    try {
      savingSwitchboard.value = true;

      const result = await apiCallback(newSwitchboardData.value);

      $q.notify({
        type: 'positive',
        message: `Switchboard "${newSwitchboardData.value.name}" created successfully`,
      });

      closeAddSwitchboardDialog();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create switchboard: ${error.message}`,
      });
      throw error;
    } finally {
      savingSwitchboard.value = false;
    }
  }

  // ============================================================================
  // ADD EXISTING SWITCHBOARD TO HIERARCHY
  // ============================================================================

  /**
   * Show add existing switchboard dialog
   * @param {Object} parentNode - Parent tree node
   * @param {Array} unallocated - Unallocated switchboards
   */
  function showAddExistingSwitchboardDialog(parentNode, unallocated) {
    parentSwitchboardNode.value = parentNode;
    unallocatedSwitchboards.value = unallocated;
    selectedExistingSwitchboard.value = null;

    addExistingSwitchboardDialogVisible.value = true;
  }

  /**
   * Close add existing switchboard dialog
   */
  function closeAddExistingSwitchboardDialog() {
    addExistingSwitchboardDialogVisible.value = false;
    parentSwitchboardNode.value = null;
    selectedExistingSwitchboard.value = null;
  }

  /**
   * Open add existing switchboard dialog with parent context
   * @param {Object} parentNode - Parent tree node
   * @param {Array} unallocated - Unallocated switchboards
   */
  function openAddExistingSwitchboardDialog(parentNode, unallocated) {
    showAddExistingSwitchboardDialog(parentNode, unallocated);
  }

  /**
   * Add existing switchboard to hierarchy
   * @param {Function} apiCallback - Async function to link via API
   * @returns {Promise<Object>} Updated switchboard
   */
  async function addExistingSwitchboard(apiCallback) {
    if (!selectedExistingSwitchboard.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a switchboard',
      });
      return null;
    }

    try {
      savingSwitchboard.value = true;

      const result = await apiCallback({
        switchboard_id: selectedExistingSwitchboard.value,
        parent_switchboard_id: parentSwitchboardNode.value?.data?.id || null,
      });

      $q.notify({
        type: 'positive',
        message: 'Switchboard added to hierarchy',
      });

      closeAddExistingSwitchboardDialog();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to add switchboard: ${error.message}`,
      });
      throw error;
    } finally {
      savingSwitchboard.value = false;
    }
  }

  // ============================================================================
  // SWITCHBOARD LOCATION PICKER
  // ============================================================================

  /**
   * Open location picker for switchboard
   * @param {Object} switchboard - Switchboard entity
   */
  function openLocationPickerForSwitchboard(switchboard) {

    switchboardForLocation.value = switchboard;
    locationPickerDialogVisible.value = true;
  }

  /**
   * Close location picker dialog
   */
  function closeLocationPicker() {
    locationPickerDialogVisible.value = false;
    switchboardForLocation.value = null;
  }

  /**
   * Save switchboard location
   * @param {Object} location - Location data (building_id, level_id, space_id)
   * @param {Function} apiCallback - Async function to update via API
   * @returns {Promise<Object>} Updated switchboard
   */
  async function saveSwitchboardLocation(location, apiCallback) {
    if (!location.building_id) {
      $q.notify({
        type: 'warning',
        message: 'Please select at least a building',
      });
      return null;
    }

    try {
      savingSwitchboard.value = true;

      const result = await apiCallback({
        switchboard_id: switchboardForLocation.value.id,
        ...location,
      });

      $q.notify({
        type: 'positive',
        message: 'Switchboard location updated',
      });

      closeLocationPicker();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update location: ${error.message}`,
      });
      throw error;
    } finally {
      savingSwitchboard.value = false;
    }
  }

  // ============================================================================
  // SWITCHBOARD VALIDATION
  // ============================================================================

  /**
   * Check if node can have switchboard children
   * @param {Object} node - Tree node
   * @returns {boolean}
   */
  function canAddSwitchboard(node) {
    const validTypes = ['main_switchboard', 'mechanical_switchboard', 'distribution_board'];
    return validTypes.includes(node.type);
  }

  /**
   * Get available new switchboard types for parent node
   * @param {Object} parentNode - Parent tree node
   * @returns {Array} Valid switchboard type options
   */
  function getAvailableNewSwitchboardTypes(parentNode) {
    if (!parentNode) {return [];}

    const parentType = parentNode.type;

    const allTypes = [
      {
        value: 'main_switchboard',
        label: 'Main Switchboard (MSB)',
        icon: 'electrical_services',
        color: 'red',
      },
      {
        value: 'mechanical_switchboard',
        label: 'Mechanical Services Switchboard (MSSB)',
        icon: 'settings',
        color: 'orange',
      },
      {
        value: 'distribution_board',
        label: 'Distribution Board (DB)',
        icon: 'dashboard',
        color: 'blue',
      },
      {
        value: 'ddc_panel',
        label: 'DDC Panel',
        icon: 'developer_board',
        color: 'purple',
      },
    ];

    switch (parentType) {
      case 'main_switchboard':
      case 'mechanical_switchboard':
        return allTypes.filter(
          (t) => t.value === 'distribution_board' || t.value === 'ddc_panel',
        );

      case 'distribution_board':
        return allTypes.filter((t) => t.value === 'ddc_panel');

      default:
        return [];
    }
  }

  /**
   * Validate switchboard can be child of parent
   * @param {Object} switchboard - Switchboard to add
   * @param {Object} parentNode - Parent tree node
   * @returns {Object} Can add and reason
   */
  function validateSwitchboardHierarchy(switchboard, parentNode) {
    if (!parentNode) {
      return {
        canAdd: true,
        reason: 'Adding as root node',
      };
    }

    // Check if parent can have children
    if (!canAddSwitchboard(parentNode)) {
      return {
        canAdd: false,
        reason: `${parentNode.type} cannot have child switchboards`,
      };
    }

    // Check if switchboard type is valid for parent
    const validTypes = getAvailableNewSwitchboardTypes(parentNode);
    const isValidType = validTypes.some((t) => t.value === switchboard.classification);

    if (!isValidType) {
      return {
        canAdd: false,
        reason: `${switchboard.classification} cannot be child of ${parentNode.type}`,
      };
    }

    // Check for circular reference
    if (switchboard.id === parentNode.data?.id) {
      return {
        canAdd: false,
        reason: 'Cannot add switchboard as child of itself',
      };
    }

    return {
      canAdd: true,
      reason: '',
    };
  }

  // ============================================================================
  // SWITCHBOARD TREE OPERATIONS
  // ============================================================================

  /**
   * Get switchboard location string
   * @param {Object} switchboard - Switchboard entity
   * @returns {string} Location string
   */
  function getSwitchboardLocation(switchboard) {
    const parts = [];

    if (switchboard.building?.name) {parts.push(switchboard.building.name);}
    if (switchboard.level?.name) {parts.push(switchboard.level.name);}
    if (switchboard.space?.name) {parts.push(switchboard.space.name);}

    return parts.length > 0 ? parts.join(' > ') : 'No location';
  }

  /**
   * Count total switchboards in tree
   * @param {Array} treeNodes - Tree nodes
   * @returns {number} Total count
   */
  function countSwitchboards(treeNodes) {
    let count = 0;

    function traverse(nodes) {
      nodes.forEach((node) => {
        if (
          node.type === 'main_switchboard' ||
          node.type === 'mechanical_switchboard' ||
          node.type === 'distribution_board' ||
          node.type === 'ddc_panel'
        ) {
          count++;
        }

        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    traverse(treeNodes);
    return count;
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    addSwitchboardDialogVisible,
    addExistingSwitchboardDialogVisible,
    locationPickerDialogVisible,
    parentSwitchboardNode,
    newSwitchboardData,
    selectedExistingSwitchboard,
    unallocatedSwitchboards,
    savingSwitchboard,
    switchboardForLocation,

    // Computed
    canSaveNewSwitchboard,
    unallocatedCount,
    availableNewSwitchboardTypes,

    // Add new switchboard
    showAddSwitchboardDialog,
    closeAddSwitchboardDialog,
    openAddSwitchboardDialog,
    saveNewSwitchboard,

    // Add existing switchboard
    showAddExistingSwitchboardDialog,
    closeAddExistingSwitchboardDialog,
    openAddExistingSwitchboardDialog,
    addExistingSwitchboard,

    // Location picker
    openLocationPickerForSwitchboard,
    closeLocationPicker,
    saveSwitchboardLocation,

    // Validation
    canAddSwitchboard,
    getAvailableNewSwitchboardTypes,
    validateSwitchboardHierarchy,

    // Tree operations
    getSwitchboardLocation,
    countSwitchboards,
  };
}
